/**
 * The shells a settings-shaped view can be put in, and the confirmation dialog.
 *
 * [Modal] is Lunamux-flavoured: a dimmed backdrop, a bordered panel, a title bar,
 * and a footer whose buttons sit right. [PaneShell] is the same body and footer
 * with none of that, for a view mounted into a pane — where the pane's own
 * chrome already supplies the title, the border and the way out.
 *
 * Nothing here knows what it is wrapping, which is what lets one view be either.
 *
 * @see DialogShell
 * @see ProjectDialog
 */
package se.soderbjorn.lunicle

import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import org.w3c.dom.events.KeyboardEvent

/**
 * A modal: backdrop, panel, title, body, footer.
 *
 * @param title shown in the title bar.
 * @param onDismiss called for Escape — the one way to leave a dialog without
 *   touching a button. Wired to the same intent as Cancel, so a draft discarded
 *   with Escape is discarded the same way as one dismissed with the button.
 *   Getting that wrong would leave a draft row behind every time someone hit
 *   Escape. A click on the backdrop deliberately does *not* dismiss: a stray
 *   click in the dim area losing an in-progress dialog read as confusing, so a
 *   dialog now stays open until a button (or Escape) closes it.
 * @param isLarge for the dialogs that hold *prose* — an issue and its comments,
 *   or a comment being written. They get a fixed, generous panel whose body
 *   scrolls inside itself, rather than the default panel that grows to fit and
 *   takes the backdrop's scrollbar with it. See .modal-large.
 */
/**
 * What a settings-shaped view needs from whatever is framing it.
 *
 * Two implementations, and the point of the interface is that the view between
 * them cannot tell which it has. [ProjectDialog] and [StatisticsDialog] are
 * *panes* now (LNL-160) — a project surface opens beside the board rather than
 * over it — but "New project…" is still a modal, because there is no project to
 * hang a pane off until it exists. One view, two frames.
 *
 * @property body where the view puts its content.
 * @property footer where the view puts its buttons.
 */
interface DialogShell {
    val body: HTMLElement
    val footer: HTMLElement

    /** Build and attach into [host]. */
    fun mount(host: HTMLElement)

    /** Take it down. A no-op for a frame that has no way of its own to close. */
    fun dismiss()

    /** Rename it. Ignored by a frame whose title is somebody else's (a pane's). */
    fun setTitle(value: String)
}

/**
 * A [DialogShell] with no chrome of its own: a body and a footer, appended to
 * whatever it is mounted in.
 *
 * For a view living in a pane. The pane header already says what this is, draws
 * the border, and carries the × — so a title bar here would be a second one, and
 * a backdrop would dim the board this is meant to sit beside.
 *
 * [dismiss] and [setTitle] are deliberately no-ops rather than errors: a pane is
 * closed by its own chrome (which routes through the workspace, not through
 * here), and its title comes from the pane label. A view that calls either is not
 * wrong, it is just talking to a frame that has nothing to do about it.
 */
class PaneShell : DialogShell {
    override val body: HTMLElement = element("div", "modal-body pane-shell-body")
    override val footer: HTMLElement = element("div", "dt-modal-buttons modal-footer pane-shell-footer")

    private val root = element("div", "pane-shell")

    override fun mount(host: HTMLElement) {
        root.children(body, footer)
        host.appendChild(root)
    }

    override fun dismiss() = Unit

    override fun setTitle(value: String) = Unit
}

class Modal(
    title: String,
    private val onDismiss: () -> Unit,
    isLarge: Boolean = false,
    /**
     * An extra class on the panel, for a dialog that wants a different measure
     * from the 640px default without every dialog inheriting it. The sign-in
     * picker uses `modal-narrow`: an auth card of one field and two buttons reads
     * as stretched and empty at 640px, and a narrow, generously-padded panel is
     * what the whole web expects a sign-in box to look like.
     */
    panelClass: String = "",
) : DialogShell {
    // Each element carries the toolkit's `dt-modal*` class FIRST and a
    // lunicle class second. The dt class is what makes these dialogs
    // pixel-match the lunula/lunamux ones — surface, border, shadow, title
    // weight, button row — and track any future toolkit restyle for free
    // (exactly the pattern lunamux's own hand-rolled dialogs use). The
    // lunicle class carries only what dt deliberately doesn't: our widths,
    // the scroll-friendly backdrop, and the topmost-wins Escape query.
    private val backdrop = element("div", "dt-modal-backdrop modal-backdrop")
    private val panel = element(
        "div",
        buildString {
            append(if (isLarge) "dt-modal modal modal-large" else "dt-modal modal")
            if (panelClass.isNotEmpty()) append(' ').append(panelClass)
        },
    )
    private val titleElement = element("h2", "dt-modal-title modal-title", title)

    /** Where the caller puts the dialog's content. */
    override val body: HTMLElement = element("div", "modal-body")

    /** Where the caller puts the buttons. */
    override val footer: HTMLElement = element("div", "dt-modal-buttons modal-footer")

    private var keyListener: ((org.w3c.dom.events.Event) -> Unit)? = null

    /** Build and attach. */
    override fun mount(host: HTMLElement) {
        panel.children(titleElement, body, footer)
        backdrop.appendChild(panel)

        // A click on the backdrop deliberately does nothing. It used to dismiss,
        // but a stray click in the dim area closing a half-filled dialog was more
        // surprise than shortcut — you would lose a draft to a misclick meant for
        // the panel. A dialog now leaves only by a button or Escape, both of which
        // are deliberate. The backdrop still swallows the click (it is a full
        // overlay) so nothing behind it reacts either.

        val listener: (org.w3c.dom.events.Event) -> Unit = { event ->
            // Only the topmost modal answers Escape. Every open modal has a
            // listener on the *document*, so without this check one Escape
            // dismisses the whole stack at once — and that is not just untidy:
            //
            // The comment modal opens over the issue modal. Dismissing the issue
            // tells MainScreen the dialog closed, which cancels the comment
            // dialog's coroutine scope. The comment's own Cancel had just
            // launched a DELETE for its draft row into that scope, so the
            // request dies in flight and the draft is orphaned — invisible on
            // every board, but on the volume forever. That is what `is_draft`
            // was meant to cover for a *crash*, not for pressing Escape.
            //
            // Found by pressing Escape with both modals open and finding an
            // empty draft comment left in the database.
            if ((event as? KeyboardEvent)?.key == "Escape" && isTopmost()) onDismiss()
        }
        keyListener = listener
        // On the document, not the panel: the panel only sees the key when
        // something inside it has focus, and Escape must work regardless.
        document.addEventListener("keydown", listener)

        host.appendChild(backdrop)
    }

    /**
     * Detach, and stop listening.
     *
     * The listener removal is the part that matters: it is on the *document*, so
     * a dialog that forgot to unregister would keep answering Escape forever —
     * and after a few open/close cycles Escape would fire several dead handlers,
     * each trying to dismiss a dialog that is no longer there.
     */
    override fun dismiss() {
        keyListener?.let { document.removeEventListener("keydown", it) }
        keyListener = null
        backdrop.remove()
    }

    override fun setTitle(value: String) = titleElement.setTextIfChanged(value)

    /**
     * Is this the modal on top?
     *
     * Read from the DOM rather than tracked in a counter here, because the DOM
     * is the thing that decides: modals mount into the same host in open order,
     * so the last `.modal-backdrop` is the one the user is looking at. A counter
     * would be a second source of truth for a fact the document already holds,
     * and it would drift the first time a dialog was dismissed out of order.
     */
    private fun isTopmost(): Boolean {
        val open = document.querySelectorAll(".modal-backdrop")
        // asList(): NodeList's own `get` is nullable-Node indexing that Kotlin's
        // stdlib does not give an identity-comparable type without help.
        return open.asList().lastOrNull() === backdrop
    }
}

/**
 * "That didn't work" — for a failure with nothing to decide.
 *
 * The counterpart to [ConfirmDialog]: same shape, but it asks nothing and offers
 * one way out. It exists because the alternative was a line of red text under the
 * top bar, and a line of red text has two problems that a modal does not:
 *
 *  - **It is missable.** It appears below the thing you clicked, which is not
 *    where you are looking, and the board is busy. "Could not start a new issue."
 *    rendered while the user was already looking at the space where the dialog
 *    should have opened — so the visible outcome of the click was nothing at all.
 *  - **It is unclearable.** It sits there until something else replaces it or the
 *    view model happens to null it out, so a stale failure reads as a current
 *    one. A dialog is dismissed by the person who read it, which is the only
 *    signal that it *was* read.
 *
 * Not a `window.alert`, for the same reasons ConfirmDialog is not a confirm: it
 * blocks the event loop, and inside the lunamux.dev iframe the browser attributes
 * it to the framing page.
 *
 * @param message the sentence to show. The view models already phrase these for
 *   a human — see `userMessage` — so this never decorates or prefixes it.
 */
class AlertDialog(
    private val title: String,
    private val message: String,
    private val onDismiss: () -> Unit,
) {
    private val modal = Modal(title, onDismiss = onDismiss)

    fun mount(host: HTMLElement) {
        modal.body.appendChild(element("p", "dt-modal-message modal-message", message))
        // One button, and it is "OK" rather than "Close": there is nothing to
        // cancel, and nothing happens either way. Cancel beside OK on a dialog
        // with no decision in it invites a search for the difference.
        modal.footer.children(
            element("div", "modal-footer-spacer"),
            button("OK", "btn btn-primary") { onDismiss() },
        )
        modal.mount(host)
    }

    fun dismiss() = modal.dismiss()
}

/**
 * "Are you sure?" — for the two things that cannot be undone.
 *
 * A dialog of its own rather than `window.confirm`, and not only for looks: a
 * native confirm blocks the event loop, and inside an iframe the browser
 * attributes it to the *framing* page — so a Lunamux visitor would get a
 * mystery dialog that appears to come from lunamux.dev.
 *
 * @param destructiveLabel the confirming button's text — "Delete", never "OK".
 *   A button that says what it does is the last chance to notice.
 * @param confirmationPhrase a phrase the user must type before the destructive
 *   button arms, or null for a plain one-click confirm (LNL-107). For the biggest
 *   undoable-nothings — deleting a whole project — a click is too cheap; a phrase
 *   turns the confirmation into a second, deliberate act. Matched case-insensitively
 *   and trimmed, so it is a sentence to mean rather than a spelling test.
 */
class ConfirmDialog(
    private val title: String,
    private val message: String,
    private val destructiveLabel: String,
    private val onConfirm: () -> Unit,
    private val onCancel: () -> Unit,
    private val confirmationPhrase: String? = null,
) {
    private val modal = Modal(title, onDismiss = onCancel)

    fun mount(host: HTMLElement) {
        modal.body.appendChild(element("p", "dt-modal-message modal-message", message))

        val confirmButton = button(destructiveLabel, "btn btn-danger") {
            // Guarded as well as visually disabled: `disabled` stops the click, but
            // the guard keeps a confirm from firing if the button is somehow reached
            // while the phrase does not match.
            if (isArmed()) onConfirm()
        }

        if (confirmationPhrase != null) {
            // Echo the exact phrase so it can be read and typed, monospaced so a
            // trailing space or a lookalike character is visible. The field below
            // arms the button only once it matches.
            modal.body.appendChild(
                element("p", "dt-modal-message modal-message confirm-phrase-prompt")
                    .also { it.appendChild(element("code", "confirm-phrase", confirmationPhrase)) },
            )
            val field = textField("") { updateArmed(confirmButton) }
            typedField = field
            modal.body.appendChild(field)
            // Disabled until the phrase is typed. A destructive button that is live
            // the instant the dialog opens is the accidental double-click this whole
            // ceremony exists to prevent.
            confirmButton.disabled = true
        }

        modal.footer.children(
            button("Cancel", "btn btn-quiet") { onCancel() },
            confirmButton,
        )
        modal.mount(host)
    }

    /** The field holding the typed phrase, while the phrase gate is in use. */
    private var typedField: org.w3c.dom.HTMLInputElement? = null

    /** Whether the destructive button may fire: always, unless a phrase gates it. */
    private fun isArmed(): Boolean {
        val phrase = confirmationPhrase ?: return true
        return typedField?.value?.trim().equals(phrase, ignoreCase = true)
    }

    private fun updateArmed(confirmButton: HTMLElement) {
        (confirmButton as? org.w3c.dom.HTMLButtonElement)?.disabled = !isArmed()
    }

    fun dismiss() = modal.dismiss()
}
